// Chamadas das Entregas mensais (ações `em_*` em /comercial/propostas-geradas).
// Contrato: docs/entregas-mensais.md. O erro vem com a frase do servidor.
import axios from 'axios';
import { API_URL } from '../../config';
import { getToken } from '../../utils/auth';

export async function post<T = any>(body: Record<string, any>, timeout = 30000): Promise<T> {
  const token = await getToken();
  const r = await axios.post(`${API_URL}/comercial/propostas-geradas`, body, { headers: { Authorization: token }, timeout });
  return (r.data?.body ? JSON.parse(r.data.body) : r.data) as T;
}

export const erroDe = (e: any): string =>
  e?.response?.data?.error || e?.response?.data?.erro || e?.message || 'erro desconhecido';
/** Baseline/ajuste do mês já salvo: a tela pergunta antes de sobrescrever. */
export const jaExiste = (e: any): boolean => e?.response?.status === 409 && e?.response?.data?.codigo === 'ja_existe';
export const ehSemAcesso = (e: any): boolean => e?.response?.status === 403;

export interface Setor { chave: string; nome: string }
export type Rateio = Record<string, number>;

export interface Item {
  id: string; contrato_id: string; parcela_id: string | null; descricao: string;
  tipo: 'valor' | 'quantidade'; valor: number; quantidade_total: number | null; unidade: string | null;
  status: 'pendente' | 'entregue' | 'cancelado'; antes_do_controle: boolean; entregue_em: string | null;
  data_prevista: string | null; data_prevista_original: string | null;
  previsto_valor: number | null; previsto_quantidade: number | null; probabilidade: number | null;
  observacao: string | null; versao: number; numero_parcela: number | null; total_parcelas: number | null;
  rateio: Rateio;
  // Calculados no servidor para a competência pedida.
  entregue_valor: number; entregue_quantidade: number; entregue_mes: number; quantidade_mes: number;
  saldo: number; na_previsao: boolean; possivel: number; provavel: number;
  situacao: 'pendente' | 'atrasado' | 'entregue' | 'cancelado';
}
export interface LinhaContrato {
  contrato_id: string; numero: string; codigo: string | null; titulo: string; setor: string | null; responsavel: string | null;
  valor_total: number; acumulado: number; saldo: number; possivel: number; provavel: number; probabilidade_media: number | null;
  entregue_mes: number; saldo_a_entregar: number; diferenca_itens: number; itens: Item[];
}
export interface Foto { total_possivel: number; total_provavel: number; salvo_por: string; salvo_em: string; por_setor: Rateio }
export interface Mes {
  competencia: string; hoje: string; inicio_controle: string; pode_editar: boolean;
  linhas: LinhaContrato[];
  totais: { valor_total: number; acumulado: number; saldo: number; possivel: number; provavel: number; entregue_mes: number; saldo_a_entregar: number };
  provavel_por_setor: Rateio; realizado_por_setor: Rateio;
  meta: { valor: number | null; supermeta: number | null };
  baseline: Foto | null; ajuste: Foto | null; setores: Setor[];
}
export interface MesPainel {
  mes: number; competencia: string; origem: 'sistema' | 'planilha';
  baseline: number | null; ajuste: number | null; meta: number | null; supermeta: number | null; realizado: number | null;
  realizado_por_setor: Rateio; meta_por_setor: Rateio;
  var_ajuste_baseline: number | null; var_ajuste_meta: number | null; var_realizado_ajuste: number | null; var_realizado_meta: number | null;
}
export interface Agregado {
  rotulo: string; baseline: number | null; ajuste: number | null; meta: number | null; realizado: number | null; realizado_por_setor: Rateio;
  var_ajuste_baseline: number | null; var_ajuste_meta: number | null; var_realizado_ajuste: number | null; var_realizado_meta: number | null;
}
export interface Painel {
  ano: number; inicio_controle: string; meses: MesPainel[]; trimestres: Agregado[]; total: Agregado; media_mensal: number | null;
  meta_anual: number | null; supermeta_anual: number | null;
  acumulado: { mes: number; meta_acumulada: number; realizado_acumulado: number | null; pct_meta_anual: number | null; projecao_acumulada?: number | null }[];
  /** Média dos meses fechados (o mês em andamento não entra) aplicada aos meses restantes. */
  projecao?: { media_mensal: number | null; meses_na_media: number; fim_do_ano: number | null };
  participacao_setor: Rateio;
  necessario_por_mes: { meta: number | null; supermeta: number | null; meses_restantes: number };
  precisao: { mes: number; erro_baseline: number | null; erro_ajuste: number | null }[];
  historico: { competencia: string; tipo: string; setor: string; valor: number }[];
  metas: { ano: number; mes: number | null; setor: string | null; valor: number; supermeta: number | null }[];
  setores: Setor[]; pode_editar: boolean;
}
export interface Prazos {
  linhas: (Omit<Item, 'situacao'> & { prevista: string; situacao: 'entregue' | 'vencido' | 'no_prazo'; atraso_dias: number; numero?: string; titulo?: string; responsavel?: string })[];
  indicadores: { entregues: number; pct_no_prazo: number | null; atraso_medio_dias: number; vencidos: number; valor_vencido: number; replanejados: number };
}
export interface Cartao {
  id: string; contrato_id: string; descricao: string; valor: number; entregue_em: string | null; numero: string; titulo: string;
  responsavel: string | null; parcela_id: string | null; parcela_status: string | null; nota_fiscal_numero: string | null;
  parcela_vencimento: string | null; parcela_valor: number | null; numero_parcela: number | null; total_parcelas: number | null;
}
export interface Kanban { colunas: { chave: string; nome: string; total: number; cartoes: Cartao[] }[] }
export interface LinhaConciliacao {
  codigo: string | null; rotulos: string[]; contrato_id: string | null; numero_erp: string | null; titulo_erp: string | null;
  planilha: { valor_total: number; entregue_acumulado: number; saldo: number; setores: Rateio | null };
  erp: { valor_total: number; parcelas_total: number; faturado: number; abertura: number | null; entregue_sistema: number; itens: number; setores: Rateio } | null;
  diferenca_valor_total: number | null; problemas: string[];
}
export interface Conciliacao { linhas: LinhaConciliacao[]; resumo: { linhas_planilha: number; contratos: number; sem_contrato: number; com_problema: number } }
export interface ParcelaContrato {
  id: string; numero_parcela: number | null; total_parcelas: number | null; titulo: string | null; valor: number;
  vencimento: string; status: string; liberada: boolean; nota_fiscal_numero: string | null; contrato_numero: string; item_id: string | null;
}
export interface Lancamento {
  id: string; origem: string; data: string; valor: number; quantidade: number | null; observacao: string | null;
  criado_por: string; criado_em: string; estornado_em: string | null; estornado_por: string | null;
}

export const obterMes = (competencia: string) => post<Mes>({ action: 'em_mes', competencia });
export const obterPainel = (ano: number) => post<Painel>({ action: 'em_painel', ano });
export const obterPrazos = (ano: number) => post<Prazos>({ action: 'em_prazos', ano });
export const obterKanban = () => post<Kanban>({ action: 'em_kanban' });
export const buscarContratos = (q: string) => post<{ contratos: { id: string; numero: string; titulo: string; valor_total: number; status: string }[] }>({ action: 'em_contratos_busca', q });
export const parcelasDoContrato = (contrato_id: string) => post<{ parcelas: ParcelaContrato[] }>({ action: 'em_contrato_parcelas', contrato_id });
export const puxarParcelas = (contrato_id: string) => post<{ criados: number; antes_do_controle: number }>({ action: 'em_itens_puxar_parcelas', contrato_id });
export const salvarItem = (campos: Record<string, any>) => post<{ item: Item }>({ action: 'em_item_salvar', ...campos });
export const excluirItem = (id: string) => post({ action: 'em_item_excluir', id });
export const entregar = (campos: { item_id: string; data?: string; valor?: number | null; quantidade?: number; observacao?: string }) =>
  post<{ valor: number; item_entregue: boolean }>({ action: 'em_entregar', ...campos });
export const lancamentosDoItem = (item_id: string) => post<{ lancamentos: Lancamento[] }>({ action: 'em_item_lancamentos', item_id });
export const estornar = (id: string) => post({ action: 'em_lancamento_estornar', id });
export const fotografar = (competencia: string, tipo: 'baseline' | 'ajuste', sobrescrever = false) =>
  post<{ total_provavel: number; total_possivel: number; itens: any[]; sobrescreveu: boolean }>({ action: 'em_fotografar', competencia, tipo, sobrescrever });
export const salvarMetas = (ano: number, metas: { mes: number | null; setor: string | null; valor: number; supermeta?: number | null }[]) =>
  post({ action: 'em_metas_salvar', ano, metas });
export const salvarHistorico = (linhas: { competencia: string; tipo: string; setor: string; valor: number | null }[]) =>
  post<{ gravadas: number; apagadas: number }>({ action: 'em_historico_salvar', linhas });
export const salvarAbertura = (contrato_id: string, valor: number, observacao?: string) =>
  post({ action: 'em_abertura_salvar', contrato_id, valor, observacao });
export const removerDoControle = (contrato_id: string) =>
  post<{ itens: number; lancamentos: number; valor_vivo: number }>({ action: 'em_contrato_remover_controle', contrato_id });
export interface PendenciaItem {
  item_id: string; versao: number; contrato_id: string; numero: string; titulo: string;
  descricao: string; valor: number; saldo: number; observacao: string | null;
}
export interface PendenciaContrato {
  contrato_id: string; numero: string; titulo: string; responsavel: string | null;
  valor_total: number; acumulado: number; saldo_a_entregar: number; itens_em_aberto: number; diferenca: number; leitura: string;
  itens: { id: string; versao: number; descricao: string; saldo: number; parcela_status: string | null }[];
}
export interface AbaConciliacaoDados extends Partial<Conciliacao> {
  foto: { id: string; aba: string | null; qtd_linhas: number; criado_por: string; criado_em: string } | null;
  pendencias: { a_conciliar: PendenciaItem[]; diferencas: PendenciaContrato[] };
}
/** Abre a aba: última foto da planilha conciliada contra o ERP de agora + pendências do controle. */
export const obterConciliacao = () => post<AbaConciliacaoDados>({ action: 'em_conciliacao' }, 60000);
/** Grava a foto colada (vira a referência da aba) e devolve a conciliação dela. */
export const gravarConciliacao = (aba: string, linhas: { rotulo: string; valor_total: any; entregue_acumulado: any; saldo: any; equipe: string }[]) =>
  post<Conciliacao & { foto: AbaConciliacaoDados['foto'] }>({ action: 'em_conciliacao_gravar', aba, linhas }, 60000);
export const conciliar = (linhas: { rotulo: string; valor_total: any; entregue_acumulado: any; saldo: any; equipe: string }[]) =>
  post<Conciliacao>({ action: 'em_conciliar', linhas }, 60000);
